import { ChevronDown, X } from 'lucide-react'
import type { DateRange } from 'react-day-picker'
import { Calendar } from '@/components/ui/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/utils/cn'

interface DateRangeFilterProps {
  value: DateRange | undefined
  onValueChange: (value: DateRange | undefined) => void
  placeholder?: string
  className?: string
}

// Short label for the trigger, e.g. "Sep 9 - Sep 10" or "Sep 9 - Jan 3, 2027"
// when the range spans years. Avoids repeating the year on both ends.
function formatRangeLabel(from: Date, to: Date | undefined): string {
  const sameYear = to && from.getFullYear() === to.getFullYear()
  const fromLabel = from.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: sameYear ? undefined : 'numeric',
  })
  if (!to) return fromLabel
  const toLabel = to.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
  return `${fromLabel} - ${toLabel}`
}

export function DateRangeFilter({
  value,
  onValueChange,
  placeholder = 'Date range',
  className = 'w-[200px]',
}: DateRangeFilterProps) {
  const label = value?.from
    ? formatRangeLabel(value.from, value.to)
    : placeholder

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex h-9 items-center justify-between rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm ring-offset-background',
            'focus:outline-none focus:ring-1 focus:ring-ring',
            'cursor-pointer',
            className
          )}
        >
          <span
            className={cn('truncate', !value?.from && 'text-muted-foreground')}
          >
            {label}
          </span>
          <div className="flex items-center shrink-0 ml-1">
            {value?.from && (
              <span
                role="button"
                tabIndex={0}
                className="rounded-sm p-0.5 hover:bg-muted cursor-pointer"
                onClick={e => {
                  e.stopPropagation()
                  onValueChange(undefined)
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.stopPropagation()
                    onValueChange(undefined)
                  }
                }}
              >
                <X className="h-3 w-3 text-muted-foreground" />
              </span>
            )}
            <ChevronDown className="h-4 w-4 opacity-50" />
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="range"
          selected={value}
          onSelect={onValueChange}
          numberOfMonths={2}
          defaultMonth={value?.from}
        />
      </PopoverContent>
    </Popover>
  )
}
